import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import SendbirdCall, { LoggerLevel, RoomType, TokenType } from "sendbird-calls";
import { reducer } from "./reducer";
import { initialState } from "./state";
import {
  createDialParams,
  handleCallError,
  statefyDirectCall,
  validatePermissions,
} from "./statefy";
import { CallContext, initialContext } from "Messenger/calling/context";
import { useSelector } from "react-redux";
import { getFullName } from "Messenger/pages/TeamConversation/utils";
import {
  useSendbirdStateContext,
  sendbirdSelectors,
} from "@sendbird/uikit-react";
import {
  createCallLog,
  createSendbirdUser,
  getSendbirdUserDetail,
  updateSendbirdUserStatus,
} from "repositories/chat-repository";
import { handleError } from "utils";
import { ConnectionHandler } from "@sendbird/chat";
import { getFCMToken } from "../../../../firebase";
import { withTranslation } from "react-i18next";
import { useAuth } from "hooks/useAuth";
import { handleWakePhaseAuthFailure } from "utils/handleWakePhaseAuthFailure";
import SendbirdNotificationRuntime from "Messenger/components/SendbirdNotificationRuntime";
// import { getFCMToken } from "firebase";

/**
 * Provider
 * ```tsx
 * <SbCallsProvider>
 *   <MyApp />
 * </Auth0Provider>
 * ```
 *
 * Provides the SbCallsProvider to its child components.
 */

const SbCallsProvider = ({ children, t }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
  });
  const [connection, setConnection] = useState("");
  const profile = useSelector((state) => state.userProfile.profile);
  const { signinHandler } = useAuth();
  // Prevents redundant SendbirdCall.authenticate() calls (which each trigger a
  // /users/{id}/login request and downstream missed_signal polling). We only
  // re-authenticate when the user changes or after an explicit deauth.
  const authInFlightRef = useRef(false);
  const authenticatedUserIdRef = useRef(null);
  const context = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(context);
  const { calls } = state;
  const currentCall = useMemo(
    () => calls.find((call) => !call.isEnded),
    [calls]
  );

  const isBusy = useMemo(() => calls.some((call) => !call.isEnded), [calls]);
  const init = useCallback((nAppId) => {
    const listenerId = "device-change-listener";
    try {
      SendbirdCall.removeListener(listenerId);
    } catch (error) {}
    SendbirdCall.init(nAppId);
    SendbirdCall.setLoggerLevel(LoggerLevel.ERROR);
    dispatch({
      type: "APP_INITIALIZED",
    });
    SendbirdCall.updateMediaDevices({ audio: true, video: true });
    SendbirdCall.addListener(listenerId, {
      onRinging: (call) => {},
      onAudioInputDeviceChanged: (current, available) => {
        dispatch({
          type: "UPDATE_AUDIO_INPUT_DEVICE_INFO",
          payload: { current, available },
        });
      },
      onAudioOutputDeviceChanged: (current, available) => {
        dispatch({
          type: "UPDATE_AUDIO_OUTPUT_DEVICE_INFO",
          payload: { current, available },
        });
      },
      onVideoInputDeviceChanged: (current, available) => {
        dispatch({
          type: "UPDATE_VIDEO_INPUT_DEVICE_INFO",
          payload: { current, available },
        });
      },
    });
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_SENDBIRD_APP_ID)
      init(process.env.REACT_APP_SENDBIRD_APP_ID);
  }, []);

  const ringingListenerId = "sb-call-listener";
  const connectionListenerId = "connection-listener";
  const auth = useCallback(
    async (authOption) => {
      try {
        
        const user = await SendbirdCall.authenticate(authOption);
  
        sdk.addConnectionHandler(
          connectionListenerId,
          new ConnectionHandler({
            onConnected: () => {
              setConnection("connected");
            },
            onDisconnected: () => {
              setConnection("disconnected");
            },
            onReconnectStarted: () => {
              setConnection("reconnectingStarted");
            },
            onReconnectSucceeded: () => {
              setConnection("reconnectingSucceeded");
            },
            onReconnectFailed: () => {
              setConnection("reconnectingFailed");
            },
          })
        );
        SendbirdCall.addListener(ringingListenerId, {
          onRinging: (call) => {
            dispatch({
              type: "RINGING",
              payload: statefyDirectCall(call, dispatch),
            });
          },
        });
  
        await SendbirdCall.connectWebSocket();
        const notificationPermission = await Notification.requestPermission();
        if (notificationPermission === "granted") {
          const token = await getFCMToken();
          if (token) {
            await SendbirdCall.unregisterAllPushTokens(TokenType.FCM);
            await SendbirdCall.registerPushToken(token, TokenType.FCM);
          }
        }
        dispatch({ type: "AUTH", payload: user });
        return user;
      } catch (error) {
        console.error("sendbird auth error", error);
        handleWakePhaseAuthFailure(error, signinHandler);
      }
    },
    [sdk, signinHandler]
  );

  const deauth = useCallback(() => {
    SendbirdCall.removeListener(ringingListenerId);
    sdk.removeConnectionHandler(connectionListenerId);
    SendbirdCall.deauthenticate();
    authenticatedUserIdRef.current = null;
    authInFlightRef.current = false;
    dispatch({ type: "DEAUTH" });
  }, []);

  /*
    Media Device Control
   */
  const updateMediaDevices = useCallback((constraints) => {
    SendbirdCall.updateMediaDevices(constraints);
  }, []);

  const selectAudioInputDevice = useCallback((mediaInfo) => {
    SendbirdCall.selectAudioInputDevice(mediaInfo);
    dispatch({
      type: "UPDATE_AUDIO_INPUT_DEVICE_INFO",
      payload: { current: mediaInfo },
    });
  }, []);

  const selectAudioOutputDevice = useCallback((mediaInfo) => {
    SendbirdCall.selectAudioOutputDevice(mediaInfo);
    dispatch({
      type: "UPDATE_AUDIO_OUTPUT_DEVICE_INFO",
      payload: { current: mediaInfo },
    });
  }, []);

  const selectVideoInputDevice = useCallback((mediaInfo) => {
    SendbirdCall.selectVideoInputDevice(mediaInfo);
    dispatch({
      type: "UPDATE_VIDEO_INPUT_DEVICE_INFO",
      payload: { current: mediaInfo },
    });
  }, []);
  const createUserLoadingRef = useRef(false);
  const userCreateOnSendBird = async (selectedUser, initialLoad = true) => {
    if (createUserLoadingRef.current && initialLoad) return;
    const SU_id = selectedUser?.id?.toString() || "";
    const name = getFullName(selectedUser);
    const profilePic = selectedUser?.profilePic || "";
    //Check if selected user has active acc in sendbird chat
    const userListQueryParam = {
      limit: 1,
      userIdsFilter: [SU_id],
    };
    const userListQuery =
      sdk.createApplicationUserListQuery(userListQueryParam);
    const sendbirdUser = await userListQuery.next();
    const checkIfuserExists = sendbirdUser.find((s) => s.userId === SU_id);
    createUserLoadingRef.current = true;
    if (!checkIfuserExists) {
      const userData = {
        user_id: SU_id,
        nickname: name,
        profile_url: profilePic || "",
      };
      await createSendbirdUser(userData);
    }
    return { ...selectedUser, SU_id };
  };
  useEffect(() => {
    if (profile.id && sdk && "createApplicationUserListQuery" in sdk) {
      void userCreateOnSendBird(profile).catch((error) => {
        handleWakePhaseAuthFailure(error, signinHandler);
      });
    }
    // Only re-run when the Sendbird user identity changes, not on every profile
    // field update (name/avatar are synced via SendbirdProvider props).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, sdk, signinHandler]);

  function handleDialCallback(call, error) {
    if (error) {
      handleError(new Error(t("patients.thisPatientIsUnavailableToTakeCall")));
      return;
    }

    createCallLog({
      sendbirdCallId: call.callId,
      appointmentId: state.appointmentDetail?.id,
    });

    const statefulCall = statefyDirectCall(call, dispatch);
    dispatch({ type: "ADD_CALL", payload: statefulCall });
    return statefulCall;
  }

  /*
    Direct Calls
   */
  const dial = useCallback(
    async function (isVideoCall, callOption) {
      if (!sdk) return;

      try {
        await validatePermissions(isVideoCall, t);

        const selectedUser = await userCreateOnSendBird(
          state.appointmentDetail.patient,
          false
        );

        const dialParams = createDialParams(
          selectedUser,
          isVideoCall,
          callOption,
          state
        );
        SendbirdCall.dial(dialParams, handleDialCallback);
      } catch (error) {
        handleCallError(error);
      }
    },
    [sdk, state?.appointmentDetail, calls]
  );

  const clearCalls = useCallback(() => {
    dispatch({ type: "CLEAR_CALLS" });
  }, []);

  const callContext = {
    ...initialContext,
    ...state,
    profile,
    init,
    auth,
    deauth,
    sdk,
    // sendbird connection
    connection,
    // callHistory,
    isAuthenticated: !!state.user,

    // Media Device Control
    updateMediaDevices,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,

    // Direct Calls
    currentCall,
    isBusy,
    dial,
    addDirectCallSound: SendbirdCall.addDirectCallSound,
    clearCalls,
    dispatch,
    // Rooms
    RoomType,
  };

  useEffect(() => {
    const userId = profile.id?.toString();
    if (
      !state.appIntialized ||
      !sdk ||
      !("registerFCMPushTokenForCurrentUser" in sdk) ||
      !userId
    ) {
      return;
    }
    // Skip if a login is already running or this user is already authenticated.
    if (authInFlightRef.current) return;
    if (authenticatedUserIdRef.current === userId && state.user) return;

    authInFlightRef.current = true;
    void auth({ userId })
      .then((user) => {
        if (user) {
          authenticatedUserIdRef.current = userId;
        }
      })
      .catch((error) => {
        handleWakePhaseAuthFailure(error, signinHandler);
      })
      .finally(() => {
        authInFlightRef.current = false;
      });
  }, [state.appIntialized, sdk, auth, profile.id, signinHandler, state.user]);
  useEffect(() => {
    if (profile.contactNumber) {
      getSendbirdUserDetail(profile.id)
        .then((res) => {
          if (
            !res.metadata?.phone_no ||
            profile.contactNumber !== res.metadata?.phone_no
          ) {
            return updateSendbirdUserStatus(profile.id, {
              phone_no: profile.contactNumber,
            });
          }
          return undefined;
        })
        .catch((error) => {
          handleWakePhaseAuthFailure(error, signinHandler);
        });
    }
  }, [profile.contactNumber, profile.id, signinHandler]);
  return (
    <CallContext.Provider value={callContext}>
      <SendbirdNotificationRuntime>{children}</SendbirdNotificationRuntime>
    </CallContext.Provider>
  );
};

export default withTranslation()(memo(SbCallsProvider));
